import React from 'react';
import classnames from 'classnames';
import Wahanda from 'common/wahanda';
import { SideBarAnalytics } from 'common/analytics';
import { phoneSupportUrl, referSalonUrl } from 'utilities/support-links';
import style from './SidebarInfo.scss';
import { GetHelpButton } from 'components/common/Sidebar/SidebarInfo/GetHelpButton';
import { WhatsNewNewsfeed } from 'components/common/Sidebar/SidebarInfo/WhatsNewNewsfeed';
import { ChatSourceType } from 'src/tracking-container/events/chat';
import { Reconnect } from 'src/federation/Reconnect';

const LANG = Wahanda.lang.home.contacts;

export interface Props {
  venueId: number;
  accountId: number;
  employeeName: string;
  emailAddress: string;
  liveChatSource: ChatSourceType;
  isSelfOnboarding: boolean;
  phoneNumber: string;
  shouldShowSupportPhoneNumber: boolean;
  isPhoneSupportEnabled: boolean;
  isHelpMenuEnabled: boolean;
}

interface SidebarInfoLink {
  className: string;
  dataTest: string;
  href?: string;
  onClick: () => void;
  label: string;
}

export class SidebarInfo extends React.Component<Props> {
  // Kept stable: Reconnect remounts its root whenever `props` changes identity.
  private phoneSupportButtonProps = {
    phoneSupportUrl: phoneSupportUrl(),
  };

  private renderLink = ({ className, dataTest, href, onClick, label }: SidebarInfoLink) => {
    let optionalProps = {};
    if (href) {
      optionalProps = {
        target: '_blank',
        rel: 'noopener noreferrer', // https://developers.google.com/web/tools/lighthouse/audits/noopener
      };
    }

    return (
      <a
        data-test={`sidebar-info-link-${dataTest}`}
        className={classnames(style.link, className)}
        key={label}
        href={href}
        role="link"
        onClick={onClick}
        {...optionalProps}
      >
        {label}
      </a>
    );
  };

  public openBookingForm() {
    window.open(LANG.callBookingUrl, '_blank', 'width=900,height=600,top=200,left=230');
  }

  public render() {
    if (this.props.isHelpMenuEnabled) {
      return null;
    }

    // This logic should be moved out with Sidebar refactor to have
    // a clean component that wouldn't depend on translations
    const href = referSalonUrl({
      venueId: this.props.venueId,
      venueName: this.props.employeeName,
      emailAddress: this.props.emailAddress,
      accountId: this.props.accountId,
    });

    const links = [
      {
        className: style.salon,
        dataTest: 'salon',
        href,
        onClick: SideBarAnalytics.trackReferSalonClick,
        label: LANG.referSalon,
      },
      {
        className: style.help,
        dataTest: 'help',
        href: LANG.helpCenterUrl,
        onClick: SideBarAnalytics.trackHelpCenterClick,
        label: LANG.helpCenter,
      },
    ];

    return (
      <div className={style.container} data-test="onboarded-sidebar-info">
        <div>
          {links.map((prop) => this.renderLink(prop))}
          <WhatsNewNewsfeed liveChatSource={this.props.liveChatSource} />
        </div>
        <div className={style.separator} />
        {this.props.shouldShowSupportPhoneNumber && (
          <div
            data-test="sidebar-info-support-phone-number"
            className={classnames(style.link, style.phone)}
          >
            {this.props.phoneNumber}
          </div>
        )}
        <GetHelpButton source={this.props.liveChatSource} />
        {this.props.isPhoneSupportEnabled && (
          <div>
            <Reconnect name="PhoneSupportButton" props={this.phoneSupportButtonProps} />
          </div>
        )}
      </div>
    );
  }
}
